<template>
  <div class="manual-page">
    <h2 class="page-title">手工签到</h2>
    <el-card>
      <p class="intro">下面先列出一部分大课报名学员，可直接勾选。人多时用搜索：支持学员姓名、拼音、首字母（张三可搜 zs / zhang）和电话。换关键词已选会保留。单次最多 80 人。</p>
      <el-input
        v-model="keyword"
        clearable
        placeholder="学员姓名 / 拼音 / 电话"
        class="search-input"
      />
      <div class="select-bar">
        <span>已选 {{ selectedIds.length }} 人</span>
        <div class="select-actions">
          <el-button v-if="keyword.trim() && keywordMatches.length" type="text" @click="toggleSelectFiltered">
            {{ allKeywordSelected ? '取消搜索结果' : '全选搜索结果' }}
          </el-button>
          <el-button v-if="selectedIds.length" type="text" @click="selectedIds = []">清空已选</el-button>
        </div>
      </div>
      <div v-if="selectedStudents.length" class="picked">
        <el-tag
          v-for="s in selectedStudents"
          :key="s.id"
          closable
          type="success"
          class="picked-tag"
          @close="unselect(s.id)"
        >
          {{ s.name }}（家长：{{ s.parentName || '未填' }}）
        </el-tag>
      </div>
      <div class="result-list">
        <div class="section-title">{{ listTitle }}</div>
        <div v-if="!searchResults.length && !selectedStudents.length" class="empty">{{ keyword.trim() ? '没有匹配学员，换个关键词试试' : '暂无大课学员' }}</div>
        <div
          v-for="s in searchResults"
          :key="s.id"
          class="student-row"
          @click="toggle(s.id)"
        >
          <span class="check"></span>
          <div class="meta">
            <div class="name">{{ s.name }}（家长：{{ s.parentName || '未填' }}）</div>
            <div class="sub">大课剩 {{ s.remainingHours || 0 }}<template v-if="s.owedHours > 0"> · 欠 {{ s.owedHours }}</template></div>
          </div>
        </div>
        <el-button v-if="hasMorePreview" type="text" @click="showMorePreview" style="width:100%">
          再显示一些（还有 {{ moreCount }} 人）
        </el-button>
      </div>

      <el-form label-width="90px" class="manual-form">
        <el-form-item label="每人课时">
          <el-input-number v-model="hours" :min="0.5" :max="20" :step="0.5" :precision="1" />
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model="remark" type="textarea" :rows="2" placeholder="选填，默认「手工签到」" />
        </el-form-item>
        <el-form-item>
          <el-button type="success" :loading="loading" :disabled="loading" @click="handleSubmit" style="width:100%">
            确认签到（{{ selectedIds.length }} 人）
          </el-button>
        </el-form-item>
      </el-form>
    </el-card>
  </div>
</template>

<script>
import { getStudents, batchManualCheckIn } from '../api'
import pinyinSearch from '../utils/pinyin-search'

export default {
  name: 'ManualCheckIn',
  data() {
    return {
      students: [],
      selectedIds: [],
      keyword: '',
      previewLimit: pinyinSearch.PREVIEW_SIZE,
      hours: 1,
      remark: '',
      loading: false
    }
  },
  computed: {
    selectedStudents() {
      const set = {}
      this.selectedIds.forEach(id => { set[id] = true })
      return this.students.filter(s => set[s.id])
    },
    keywordMatches() {
      const kw = (this.keyword || '').trim()
      if (!kw) return []
      return this.students.filter(s => pinyinSearch.matchStudent(s, kw))
    },
    searchResults() {
      const set = {}
      this.selectedIds.forEach(id => { set[id] = true })
      const kw = (this.keyword || '').trim()
      const pool = kw ? this.keywordMatches : this.students
      const unselected = pool.filter(s => !set[s.id])
      return kw ? unselected : unselected.slice(0, this.previewLimit)
    },
    moreCount() {
      if ((this.keyword || '').trim()) return 0
      const set = {}
      this.selectedIds.forEach(id => { set[id] = true })
      return Math.max(0, this.students.filter(s => !set[s.id]).length - this.searchResults.length)
    },
    hasMorePreview() {
      return this.moreCount > 0
    },
    listTitle() {
      const kw = (this.keyword || '').trim()
      if (kw) return '搜索结果 ' + this.searchResults.length
      const shown = this.selectedStudents.length + this.searchResults.length
      return '大课报名学员（先显示 ' + shown + ' / 共 ' + this.students.length + ' 人）'
    },
    allKeywordSelected() {
      return this.keywordMatches.length > 0 && this.keywordMatches.every(s => this.selectedIds.indexOf(s.id) >= 0)
    }
  },
  mounted() {
    this.loadStudents()
  },
  methods: {
    async loadStudents() {
      const all = []
      let page = 1
      const size = 200
      while (page <= 30) {
        const res = await getStudents({ page, size, courseType: 1 })
        const records = (res.data && res.data.records) || []
        const total = Number((res.data && res.data.total) || 0)
        all.push.apply(all, records)
        if (!records.length || all.length >= total || records.length < size) break
        page++
      }
      this.students = all
      if (this.$route.query.id) {
        const id = Number(this.$route.query.id)
        if (id && this.selectedIds.indexOf(id) < 0) this.selectedIds = [id]
      }
    },
    toggle(id) {
      const i = this.selectedIds.indexOf(id)
      if (i >= 0) this.selectedIds.splice(i, 1)
      else this.selectedIds.push(id)
    },
    unselect(id) {
      this.selectedIds = this.selectedIds.filter(x => x !== id)
    },
    showMorePreview() {
      this.previewLimit += pinyinSearch.PREVIEW_SIZE
    },
    toggleSelectFiltered() {
      const ids = this.keywordMatches.map(s => s.id)
      if (!ids.length) return
      if (this.allKeywordSelected) {
        const drop = {}
        ids.forEach(id => { drop[id] = true })
        this.selectedIds = this.selectedIds.filter(id => !drop[id])
      } else {
        const have = {}
        this.selectedIds.forEach(id => { have[id] = true })
        ids.forEach(id => { if (!have[id]) this.selectedIds.push(id) })
      }
    },
    async handleSubmit() {
      if (this.loading) return
      if (!this.selectedIds.length) {
        this.$message.warning('请勾选到课学员')
        return
      }
      if (this.selectedIds.length > 80) {
        this.$message.warning('单次最多 80 人，请分批签到')
        return
      }
      const hours = Number(this.hours)
      if (!Number.isFinite(hours) || hours < 0.5 || hours > 20 || Math.abs(hours * 2 - Math.round(hours * 2)) > 0.001) {
        this.$message.warning('每人课时须为 0.5～20，且为 0.5 的倍数')
        return
      }
      this.loading = true
      try {
        const res = await batchManualCheckIn({
          studentIds: this.selectedIds,
          hours: hours.toFixed(1),
          remark: (this.remark || '').trim() || '手工签到'
        })
        const data = res.data || {}
        this.$alert(data.message || ('已处理 ' + this.selectedIds.length + ' 人，每人扣 ' + hours + ' 课时'), '签到完成')
          .then(() => {
            this.selectedIds = []
            this.keyword = ''
            this.remark = ''
            this.loadStudents()
          })
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.page-title { margin-bottom: 20px; }
.intro { font-size: 13px; color: #64748B; margin: 0 0 16px; line-height: 1.6; }
.search-input { margin-bottom: 12px; }
.select-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  font-size: 13px;
  color: #64748B;
}
.picked { margin-bottom: 12px; }
.picked-tag { margin: 0 8px 8px 0; }
.section-title { font-size: 12px; color: #94A3B8; margin-bottom: 8px; }
.result-list { max-height: 360px; overflow: auto; margin-bottom: 16px; border: 1px solid #F1F5F9; border-radius: 8px; padding: 8px 12px; }
.student-row {
  display: flex;
  align-items: center;
  padding: 8px 4px;
  cursor: pointer;
  border-bottom: 1px solid #F8FAFC;
}
.student-row:hover { background: #F0FDFA; }
.check {
  width: 16px;
  height: 16px;
  border: 2px solid #94A3B8;
  border-radius: 4px;
  margin-right: 10px;
  flex-shrink: 0;
}
.meta { flex: 1; }
.name { font-size: 14px; color: #0F172A; font-weight: 600; }
.sub { font-size: 12px; color: #64748B; margin-top: 2px; }
.empty { text-align: center; color: #94A3B8; padding: 16px 0; font-size: 13px; }
.manual-form { max-width: 560px; }
@media (max-width: 768px) {
  .manual-form { max-width: 100%; }
  .manual-form >>> .el-form-item__label { width: 80px !important; }
}
</style>
